import { createContext, useContext, useState, ReactNode, useMemo } from 'react'
import { useDebounce } from '@/hooks/useDebounce'
import { InventoryItem } from '../interfaces/inventory'
import { useContainersProductsQuery } from './useContainersProductsQuery'

interface LogisticInventoryContextType {
  inventoryData: InventoryItem[]
  isLoading: boolean
  isFetching: boolean
  isError: boolean
  currentPage: number
  setCurrentPage: (_page: number) => void
  pageSize: number
  setPageSize: (_size: number) => void
  searchQuery: string
  setSearchQuery: (_query: string) => void
  refetch: () => void
  filters: Record<string, unknown>
  setFilters: (_filters: Record<string, unknown>) => void
  // Información de paginación del servidor
  totalItems: number
  totalPages: number
}

const LogisticInventoryContext = createContext<
  LogisticInventoryContextType | undefined
>(undefined)

interface LogisticInventoryProviderProps {
  children: ReactNode
}

export function LogisticInventoryProvider({
  children,
}: LogisticInventoryProviderProps) {
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(20) // Valor por defecto sincronizado con backend
  const [filters, setFilters] = useState({})
  const [searchQuery, setSearchQuery] = useState('')

  // Debounce la búsqueda para evitar demasiadas llamadas al backend
  const debouncedSearchQuery = useDebounce(searchQuery, 300) // 300ms de delay optimizado

  // Resetear página a 1 cuando cambie la búsqueda (optimizado)
  const [previousSearch, setPreviousSearch] = useState('')
  if (debouncedSearchQuery !== previousSearch) {
    setPreviousSearch(debouncedSearchQuery)
    if (currentPage !== 1 && debouncedSearchQuery !== '') {
      setCurrentPage(1)
    }
  }

  // Parámetros para la query - usar pageSize seleccionado en el filtro
  const queryParams = useMemo(() => {
    const baseParams = {
      page: currentPage,
      limit: pageSize, // Usar exactamente la cantidad de filas por página seleccionada
      // Enviar búsqueda al backend cuando hay término de búsqueda
      ...(debouncedSearchQuery.trim() && {
        search: debouncedSearchQuery.trim(),
      }),
    }

    // Transform complex filters to API parameters
    const transformedFilters: Record<string, unknown> = {}

    // Handle supplier filter
    const supplierFilter = (filters as Record<string, unknown>).supplier
    if (supplierFilter && supplierFilter !== '' && supplierFilter !== 'todos') {
      transformedFilters.supplierIds = supplierFilter
    }

    // Handle expiration filter
    // Map expiration categories to date ranges (defaults hardcoded here).
    // Assumptions (change later when backend provides defaults):
    // - urgente: from today to today + 7 days
    // - alerta: from today + 8 days to today + 30 days
    // - normal: from today + 31 days with no upper limit
    const expirationFilter = (filters as Record<string, unknown>).expiration as
      | {
          todos?: boolean
          urgente?: boolean
          alerta?: boolean
          normal?: boolean
        }
      | undefined
    if (expirationFilter && typeof expirationFilter === 'object') {
      if (!expirationFilter.todos) {
        // Build date ranges based on selected categories. Multiple categories
        // will expand the overall from/to range to include all selected days.
        // Normalizar la fecha a medianoche para consistencia en el caché
        const now = new Date()
        now.setHours(0, 0, 0, 0)

        const ranges: { from: Date; to?: Date }[] = []

        if (expirationFilter.urgente) {
          const from = new Date(now)
          const to = new Date(now)
          to.setDate(to.getDate() + 7)
          ranges.push({ from, to })
        }

        if (expirationFilter.alerta) {
          const from = new Date(now)
          from.setDate(from.getDate() + 8)
          const to = new Date(now)
          to.setDate(to.getDate() + 30)
          ranges.push({ from, to })
        }

        if (expirationFilter.normal) {
          // 'normal' means more than 31 days with no upper limit. Represent
          // it by providing a from date and leaving to undefined.
          const from = new Date(now)
          from.setDate(from.getDate() + 31)
          ranges.push({ from })
        }

        if (ranges.length > 0) {
          // Special-case: if the user selected ONLY 'urgente', send only
          // expirationDateTo (today + 7 days). This matches the requested
          // behavior where 'urgente' should be a 'to' filter.
          const onlyUrgenteSelected =
            expirationFilter.urgente &&
            !expirationFilter.alerta &&
            !expirationFilter.normal

          if (onlyUrgenteSelected) {
            const now = new Date()
            now.setHours(0, 0, 0, 0)
            const to = new Date(now)
            to.setDate(to.getDate() + 7)
            // Format as YYYY-MM-DD without time
            const year = to.getFullYear()
            const month = String(to.getMonth() + 1).padStart(2, '0')
            const day = String(to.getDate()).padStart(2, '0')
            transformedFilters.expirationDateTo = `${year}-${month}-${day}`
          } else {
            const earliest = ranges.reduce(
              (acc, r) => (r.from < acc ? r.from : acc),
              ranges[0].from
            )

            const hasOpenUpper = ranges.some((r) => r.to === undefined)

            let latest: Date
            if (hasOpenUpper) {
              latest = new Date('9999-12-31T23:59:59.999Z')
            } else {
              const rangesWithTo = ranges.filter((r) => r.to !== undefined) as {
                from: Date
                to: Date
              }[]
              latest = rangesWithTo.reduce(
                (acc, r) => (r.to > acc ? r.to : acc),
                rangesWithTo[0].to
              )
            }

            // Format as YYYY-MM-DD without time
            const fromYear = earliest.getFullYear()
            const fromMonth = String(earliest.getMonth() + 1).padStart(2, '0')
            const fromDay = String(earliest.getDate()).padStart(2, '0')
            transformedFilters.expirationDateFrom = `${fromYear}-${fromMonth}-${fromDay}`

            const toYear = latest.getFullYear()
            const toMonth = String(latest.getMonth() + 1).padStart(2, '0')
            const toDay = String(latest.getDate()).padStart(2, '0')
            transformedFilters.expirationDateTo = `${toYear}-${toMonth}-${toDay}`
          }
        }
      }
    }

    return {
      ...baseParams,
      ...transformedFilters,
    }
  }, [currentPage, pageSize, debouncedSearchQuery, filters])

  // Usar el nuevo hook para containers/products
  const { inventoryData, pagination, isLoading, isFetching, isError, refetch } =
    useContainersProductsQuery({
      params: queryParams,
    })

  // Calcular total de páginas basado en la respuesta del servidor
  const totalItems = pagination?.total || 0
  const totalPages = Math.ceil(totalItems / pageSize)

  const handleSetPageSize = (size: number) => {
    setPageSize(size)
    // Reset to first page when changing page size (if not already on page 1)
    if (currentPage !== 1) {
      setCurrentPage(1)
    }
  }

  // El backend ahora maneja la búsqueda, no necesitamos filtrado local

  return (
    <LogisticInventoryContext.Provider
      value={{
        inventoryData: inventoryData || [],
        isLoading,
        isFetching,
        isError,
        currentPage,
        setCurrentPage,
        pageSize,
        setPageSize: handleSetPageSize,
        searchQuery,
        setSearchQuery,
        refetch,
        filters,
        setFilters,
        totalItems,
        totalPages,
      }}
    >
      {children}
    </LogisticInventoryContext.Provider>
  )
}

export function useLogisticInventory() {
  const context = useContext(LogisticInventoryContext)
  if (context === undefined) {
    throw new Error(
      'useLogisticInventory must be used within a LogisticInventoryProvider'
    )
  }
  return context
}
